import { useEffect, useRef } from 'react';

const steps = [
  {
    num: '01',
    title: 'Private Consultation',
    description:
      'Define your goals, timeline, and property criteria or sale objectives in a confidential one-on-one meeting tailored to your unique situation in the Lake Tahoe or Sierra Nevada market.',
    icon: 'ri-discuss-line',
  },
  {
    num: '02',
    title: 'Strategy Development',
    description:
      'A fully bespoke plan for acquisition or sale — including precision pricing, targeted positioning, and maximum market exposure across the Sierra Nevada luxury landscape.',
    icon: 'ri-draft-line',
  },
  {
    num: '03',
    title: 'Expert Execution',
    description:
      'Curated property search or a premium listing launch, backed by expert negotiation and hands-on transaction management from contract to close.',
    icon: 'ri-award-line',
  },
  {
    num: '04',
    title: 'Closing & Beyond',
    description:
      'A seamless, stress-free closing experience with continued advisory support and market guidance long after the transaction is complete.',
    icon: 'ri-key-2-line',
  },
];

export default function ProcessSection() {
  const stepsRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const items = stepsRef.current?.querySelectorAll('.process-step');
    if (!items) return;
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            (entry.target as HTMLElement).style.opacity = '1';
            (entry.target as HTMLElement).style.transform = 'translateY(0)';
          }
        });
      },
      { threshold: 0.1 }
    );
    items.forEach((item, i) => {
      const el = item as HTMLElement;
      el.style.opacity = '0';
      el.style.transform = 'translateY(50px)';
      el.style.transition = `opacity 0.8s cubic-bezier(0.16,1,0.3,1) ${0.1 + i * 0.12}s, transform 0.8s cubic-bezier(0.16,1,0.3,1) ${0.1 + i * 0.12}s`;
      observer.observe(el);
    });
    return () => observer.disconnect();
  }, []);

  return (
    <section className="relative z-10 py-28 px-4 md:px-8 overflow-hidden">
      {/* Cinematic background */}
      <div
        className="absolute inset-0 bg-cover bg-center"
        style={{
          backgroundImage:
            'url(https://public.readdy.ai/ai/img_res/63fcaecb1888cdd71a98d1ab4705bff2.jpg)',
        }}
      />
      <div className="absolute inset-0 bg-charcoal/91" />
      <div className="absolute top-0 left-0 right-0 h-px bg-gradient-to-r from-transparent via-gold/25 to-transparent" />
      <div className="absolute bottom-0 left-0 right-0 h-px bg-gradient-to-r from-transparent via-gold/25 to-transparent" />

      <div className="relative z-10 max-w-7xl mx-auto">
        {/* Header */}
        <div className="text-center mb-20">
          <p className="text-[10px] tracking-[0.55em] uppercase text-gold mb-4">How It Works</p>
          <h2 className="font-serif text-5xl md:text-7xl text-white font-light">
            The <em className="italic text-gold">Process</em>
          </h2>
          <div className="flex items-center justify-center gap-3 mt-5">
            <div className="w-16 h-px bg-gold/25" />
            <div className="w-1.5 h-1.5 rotate-45 bg-gold/50" />
            <div className="w-16 h-px bg-gold/25" />
          </div>
        </div>

        {/* Steps */}
        <div ref={stepsRef} className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-8 lg:gap-6">
          {steps.map((step, i) => (
            <div key={step.num} className="process-step relative group">
              {/* Connector line */}
              {i < steps.length - 1 && (
                <div className="hidden lg:block absolute top-8 left-[62%] right-[-22%] h-px z-0"
                  style={{ background: 'linear-gradient(90deg, rgba(196,169,98,0.25), transparent)' }} />
              )}
              <div className="relative z-10">
                {/* Number + icon stack */}
                <div className="relative mb-7 w-16 h-16">
                  <span className="absolute inset-0 font-serif text-8xl text-gold/8 font-light leading-none select-none flex items-center">
                    {step.num}
                  </span>
                  <div className="absolute bottom-0 left-0 w-12 h-12 flex items-center justify-center rounded-xl border border-gold/25 bg-gold/10 group-hover:bg-gold/20 transition-all duration-300">
                    <i className={`${step.icon} text-gold text-lg`} />
                  </div>
                </div>
                <h3 className="font-serif text-xl text-white font-medium mb-3">{step.title}</h3>
                <p className="text-warm-white/45 text-sm leading-relaxed">{step.description}</p>
              </div>
            </div>
          ))}
        </div>

        {/* Bottom CTA */}
        <div className="text-center mt-20 pt-10 border-t border-white/8">
          <p className="font-serif text-xl text-white/70 font-light mb-6">
            Ready to begin? Your consultation is completely confidential.
          </p>
          <a
            href="#consultation"
            className="inline-block whitespace-nowrap px-10 py-4 bg-gold text-white text-[11px] tracking-[0.25em] uppercase font-medium cursor-pointer rounded-md hover:bg-[#b8983a] transition-all duration-300"
            style={{ boxShadow: '0 0 30px rgba(196,169,98,0.25)' }}
          >
            Begin Your Journey
          </a>
        </div>
      </div>
    </section>
  );
}
